"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SettingsSection = void 0;
const ui_1 = require("@app/component/ui");
const Dropdown_1 = require("@app/component/ui/dropdown/Dropdown");
const forms_1 = require("@app/component/ui/forms");
const dropdown_input_1 = require("@app/component/ui/forms/dropdown-input/dropdown-input");
const input_label_1 = require("@app/component/ui/forms/input-label/input-label");
const const_1 = require("@app/const");
const user_details_1 = require("@app/reducer/user-details");
const request_1 = require("@app/request");
const position_assign_update_1 = require("@app/request/position-assign-update");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const forms_2 = require("@app/util/forms/forms");
const use_modal_1 = require("@app/util/modal/use-modal");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const react_hot_toast_1 = __importDefault(require("react-hot-toast"));
const load_role_details_1 = require("../../ac/load-role-details");
const role_update_1 = require("../../ac/role-update");
const employee_card_context_1 = require("../../util/employee-card-context");
const use_terminate_employee_1 = require("../../util/hooks/use-terminate-employee");
const useEmployeeCardFooterContent_1 = require("../../util/hooks/useEmployeeCardFooterContent");
const useUserDetailsContracts_1 = require("../../util/hooks/useUserDetailsContracts");
const useUserDetailsCustomData_1 = require("../../util/hooks/useUserDetailsCustomData");
const useUserDetailsPositions_1 = require("../../util/hooks/useUserDetailsPositions");
const useWorkspaceAccessRoles_1 = require("../../util/hooks/useWorkspaceAccessRoles");
const experience_table_1 = require("../experience-table");
const hiddenOptions = [
    { value: true, label: 'Ano' },
    { value: false, label: 'Ne' },
];
const generateCycleOptions = (cycles) => cycles.flatMap((cycle) => cycle?.groups?.map((g) => ({
    value: `${cycle.id}-${g?.group ?? ''}`,
    label: `${cycle?.title ?? ''} - ${g?.name ?? ''}`,
})) ?? []);
const generateAccessOptions = (accessRoles) => accessRoles.map((role) => ({
    value: (0, maybe_unwrap_1.unwrapMaybe)(role.id),
    label: role.name,
}));
const SettingsSection = () => {
    const { workspace, employee, userId } = (0, employee_card_context_1.useEmployeeCard)();
    const { closeModal } = (0, use_modal_1.useModal)();
    const { role } = (0, store_1.useRootSelector)((state) => ({ role: state.userDetails.role }));
    const dispatch = (0, store_1.useRootDispatch)();
    const { canWrite } = (0, util_1.useAccessRole)();
    const [customData] = (0, useUserDetailsCustomData_1.useUserDetailsCustomData)();
    const [endNow, setEndNow] = react_1.default.useState();
    const [deleteDate, setDeleteDate] = react_1.default.useState();
    const { data: accessRoles, refetch: refetchAccessRoles } = (0, useWorkspaceAccessRoles_1.useWorkspaceAccessRoles)();
    const relevantAccessRoles = accessRoles.filter((ar) => ar.workspaceId === workspace?.id);
    const me = (0, store_1.useRootSelector)((state) => state.me);
    const userIsMe = me.id === employee?.user?.id;
    const allowEdit = canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES);
    const canDeleteEmployee = allowEdit && !customData?.managedByPlugin && !userIsMe;
    const originalAccessRole = react_1.default.useMemo(() => relevantAccessRoles.reduce((prev, curr) => {
        const isAssigned = curr.assigns?.some((assign) => assign?.userId === employee.user?.id);
        if (isAssigned) {
            return curr;
        }
        return prev;
    }, undefined), [relevantAccessRoles]);
    const cycleOptions = react_1.default.useMemo(() => generateCycleOptions(workspace?.cycles ?? []), [workspace?.cycles]);
    const accessOptions = react_1.default.useMemo(() => generateAccessOptions(relevantAccessRoles), [relevantAccessRoles]);
    const { control, setValue, reset, getValues, handleSubmit, formState: { isDirty, isSubmitting }, } = (0, react_hook_form_1.useForm)({
        defaultValues: {
            employeeData: {
                hidden: false,
            },
            employeeExperience: [],
        },
    });
    const [editedExperience, setEditedExperience] = react_1.default.useState([]);
    const setEdited = react_1.default.useCallback((id) => {
        setEditedExperience((prev) => [...prev, id]);
    }, [setEditedExperience]);
    const cycleIdController = (0, react_hook_form_1.useController)({
        name: 'employeeData.cycleId',
        control,
    });
    const cycleGroupController = (0, react_hook_form_1.useController)({
        name: 'employeeData.cycleGroup',
        control,
    });
    const injectValue = (0, forms_2.useInjectValue)(setValue);
    react_1.default.useEffect(() => {
        if ((0, util_1.isDefined)(originalAccessRole?.id) && (0, util_1.isNotNull)(originalAccessRole?.id)) {
            injectValue('employeeData.accessRoleId', originalAccessRole?.id);
        }
    }, [originalAccessRole]);
    react_1.default.useEffect(() => {
        const action = async () => {
            if ((0, util_1.isUndefined)(workspace?.id) || (0, util_1.isUndefined)(employee?.user?.id))
                return;
            const data = await dispatch((0, load_role_details_1.loadRoleDetails)({
                workspaceId: workspace?.id ?? '',
                userId: employee.user?.id ?? '',
            }));
            if ((0, util_1.isUndefined)(data))
                return;
            injectValue('employeeData.hidden', (0, maybe_unwrap_1.unwrapMaybe)(data.hidden) ?? false);
            injectValue('employeeData.calendarOrder', (0, maybe_unwrap_1.unwrapMaybe)(data.calendarOrder));
            injectValue('employeeData.cycleGroup', (0, maybe_unwrap_1.unwrapMaybe)(data.cycleGroup));
            injectValue('employeeData.cycleId', (0, maybe_unwrap_1.unwrapMaybe)(data.cycleId));
            setDeleteDate((0, moment_1.default)((0, maybe_unwrap_1.unwrapMaybe)(data.terminateDate)).format('YYYY-MM-DD'));
        };
        void action();
    }, [workspace?.id, employee?.user?.id]);
    const onSubmit = async (data) => {
        const { employeeData } = data;
        const { accessRoleId } = employeeData;
        const toAssign = relevantAccessRoles.find((option) => option.id === accessRoleId && accessRoleId !== originalAccessRole?.id);
        await dispatch((0, role_update_1.roleUpdate)({
            workspaceId: workspace?.id ?? '',
            userId: employee.user?.id ?? '',
            roleData: employeeData,
            reassignAccessRole: toAssign,
        }));
        reset(getValues());
        await refetchAccessRoles();
        const _getValues = getValues();
        const filteredExperience = _getValues.employeeExperience.filter((e) => editedExperience.some((id) => id === e.position));
        filteredExperience.forEach(async (fe) => {
            const resPosAss = await (0, position_assign_update_1.mutatePositionAssignUpdate)(fe);
            if ((0, util_1.isUndefined)(resPosAss))
                return;
            const index = _getValues.employeeExperience.findIndex((ee) => ee.position === resPosAss.positionId);
            setValue(`employeeExperience.${index}`, {
                position: fe.position,
                productionForecast: resPosAss.productionForecast,
                skill: resPosAss.skill,
                user: fe.workspace,
                workspace: fe.workspace,
                contract: fe.contract,
            }, {
                shouldDirty: false,
                shouldTouch: false,
            });
        });
        react_hot_toast_1.default.custom(() => (react_1.default.createElement(ui_1.Alert, { type: ui_1.Alert.TYPES.SUCCESS, text: (0, util_1.t)('SUCCESS_MESSAGE.employeeProfile') })));
    };
    const { displayContent } = (0, useEmployeeCardFooterContent_1.useEmployeeCardFooterContent)(react_1.default.createElement("div", { style: { display: 'flex' } },
        react_1.default.createElement(ui_1.Button, { onClick: () => {
                closeModal();
                // void dispatch(setModal(null))
            } }, (0, util_1.t)('CLOSE')),
        react_1.default.createElement(ui_1.Button, { onClick: handleSubmit(onSubmit), style: 'CONTAINED', disabled: !isDirty || isSubmitting || (!userIsMe && !allowEdit) }, (0, util_1.t)('SAVE'))));
    const displayDeleteEmployeeButton = ((0, util_1.isDefined)(endNow) && endNow) ||
        ((0, util_1.isDefined)(endNow) && !endNow && (0, moment_1.default)(deleteDate).isValid());
    const removeTerminationDate = async () => {
        const res = await (0, request_1.requestRoleUpdate)({
            workspace: workspace?.id ?? '',
            user: employee.user?.id ?? '',
            data: {
                terminateDate: null,
            },
        });
        if ((0, util_1.isDefined)(res)) {
            dispatch((0, user_details_1.setRoleTerminateDate)(undefined));
        }
    };
    const employeeScheduledForTermination = (0, util_1.isDefined)(role?.terminateDate) && (0, util_1.isNotNull)(role?.terminateDate);
    const positions = (0, useUserDetailsPositions_1.useUserDetailsPositions)();
    const contracts = (0, useUserDetailsContracts_1.useUserDetailsContracts)();
    const positionAssignments = contracts
        ?.reduce((acc, curr) => {
        curr.positions
            ?.filter((pos) => positions?.some((_p) => _p.id === pos.positionId))
            .forEach((pos) => {
            acc.push({ ...pos, contractId: curr.id });
        });
        return acc;
    }, [])
        .map((pos) => ({
        positionId: pos.positionId,
        skill: pos.skill,
        contractId: pos.contractId,
        productionForecast: pos.productionForecast,
    }));
    const noDuplicates = positionAssignments?.filter((pos, index, self) => index === self.findIndex((t) => t.positionId === pos.positionId));
    const userWorkspacePositionsAssignments = positions
        ?.filter((pos) => pos.workspaceId === workspace.id && !pos.archived)
        .map(({ id, workspaceId, name, color }) => {
        const relevantPositionAssignment = noDuplicates?.find((_pos) => _pos.positionId === id);
        return {
            workspace: workspaceId ?? '',
            position: id,
            contract: relevantPositionAssignment?.contractId ?? '',
            user: employee.user?.id ?? '',
            skill: relevantPositionAssignment?.skill,
            productionForecast: relevantPositionAssignment?.productionForecast,
            name,
            color,
        };
    });
    react_1.default.useEffect(() => {
        const _values = getValues();
        if ((0, util_1.isUndefined)(userWorkspacePositionsAssignments))
            return;
        if ((0, util_1.isDefined)(_values.employeeExperience) &&
            _values.employeeExperience.length > 0)
            return;
        injectValue('employeeExperience', userWorkspacePositionsAssignments);
    }, [userWorkspacePositionsAssignments]);
    const terminateEmployeeModal = (0, use_terminate_employee_1.useTerminateEmployee)({
        userId,
        workspaceId: workspace.id,
        deleteDate,
        endNow,
        setEndNow,
    });
    const isLoadingTerminate = (0, store_1.useRootSelector)((state) => state.loading.includes(use_terminate_employee_1.USE_TERMINATE_EMPLOYEE_ACTION));
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL },
            react_1.default.createElement("div", { style: {
                    display: 'grid',
                    gridTemplateRows: 'auto',
                    gridTemplateColumns: '50%',
                    gap: '1rem',
                } },
                react_1.default.createElement(forms_1.NumberInput, { control: control, name: 'employeeData.calendarOrder', label: (0, util_1.t)('EMPOYEE_PROFILE_CALENDAR_ORDER'), disabled: !allowEdit, min: 0, max: 9999999, step: 1, integerOnly: true }),
                react_1.default.createElement(Dropdown_1.Dropdown, { style: Dropdown_1.Dropdown.STYLES.LIGHT, type: Dropdown_1.Dropdown.TYPES.VARIABLE, size: Dropdown_1.Dropdown.SIZES.FULL_WIDTH, label: (0, util_1.t)('EMPLOYEE_WORK_CYCLE'), options: cycleOptions, singleSelect: true, value: cycleOptions.find((o) => o.value ===
                        `${cycleIdController.field.value ?? 0}-${cycleGroupController.field.value ?? 0}`), onChange: (option) => {
                        const [cycleId, cycleGroup] = option?.value?.split('-') ?? [];
                        cycleIdController.field.onChange(cycleId);
                        cycleGroupController.field.onChange(parseInt(cycleGroup));
                    }, disabled: cycleIdController.field.disabled || !allowEdit }),
                react_1.default.createElement("div", null,
                    react_1.default.createElement(input_label_1.InputLabel, { label: (0, util_1.t)('ACCESS_LEVEL'), subLabel: (0, util_1.t)('EMPLOYEE_PROFILE_PRIVILEGES_EMPLOYEE') }),
                    react_1.default.createElement(dropdown_input_1.DropdownInput, { control: control, name: 'employeeData.accessRoleId', options: accessOptions, disabled: userIsMe || !allowEdit })),
                util_1.miscUtil.getFilteredContentForOrganization('employee_card_hide_emp', react_1.default.createElement("div", null,
                    react_1.default.createElement(input_label_1.InputLabel, { label: (0, util_1.t)('HIDDEN_EMPLOYEE'), subLabel: (0, util_1.t)('EMPLOYEE_PROFILE_HIDDEN_DESCRIPTION') }),
                    react_1.default.createElement(dropdown_input_1.DropdownInput, { control: control, name: 'employeeData.hidden', options: hiddenOptions, disabled: !allowEdit }))),
                react_1.default.createElement(input_label_1.InputLabel, { label: (0, util_1.t)('EMPLOYEE_PROFILE_CARD_CONTRACT_SKILL') }),
                react_1.default.createElement(experience_table_1.ExperienceTable, { setEdited: setEdited, control: control }),
                canDeleteEmployee && (react_1.default.createElement(react_1.default.Fragment, null, allowEdit && (react_1.default.createElement(input_label_1.InputLabel, { color: 'danger', label: (0, util_1.t)('DELETE_EMPLOYEE'), subLabel: (0, util_1.t)('EMPLOYEE_PROFILE_ROLE_END_TXT') })))),
                employeeScheduledForTermination && (react_1.default.createElement(ui_1.Text, { type: 'body-large' },
                    "Zam\u011Bstnanec bude smaz\u00E1n ke dni",
                    ' ',
                    (0, moment_1.default)(role?.terminateDate).format('DD. MM. YYYY'))),
                canDeleteEmployee && (react_1.default.createElement(react_1.default.Fragment, null,
                    employeeScheduledForTermination && allowEdit && (react_1.default.createElement("div", { onClick: removeTerminationDate, style: {
                            color: '#0045FF',
                            textDecoration: 'underline',
                            textDecorationColor: '#0045FF',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                        } }, (0, util_1.t)('CANCEL_EMPLOYEE_DELETION'))),
                    !employeeScheduledForTermination && allowEdit && (react_1.default.createElement(react_1.default.Fragment, null,
                        react_1.default.createElement("div", { style: { display: 'flex', gap: '0.5rem' } },
                            react_1.default.createElement(ui_1.Checkbox, { testid: 'delete-employee-checkbox', checked: (0, util_1.isDefined)(endNow) && endNow, onChange: (v) => {
                                    if (v) {
                                        setEndNow(true);
                                        return;
                                    }
                                    setEndNow(undefined);
                                } }, (0, util_1.t)('EMPLOYEE_PROFILE_ROLE_END_NOW')),
                            react_1.default.createElement(ui_1.Checkbox, { checked: (0, util_1.isDefined)(endNow) && !endNow, onChange: (v) => {
                                    if (v) {
                                        setEndNow(false);
                                        return;
                                    }
                                    setEndNow(undefined);
                                } }, (0, util_1.t)('EMPLOYEE_PROFILE_ROLE_END_AT_DATE'))),
                        react_1.default.createElement("div", { style: {
                                display: 'flex',
                                width: '100%',
                                gap: '1rem',
                                alignItems: 'end',
                            } },
                            (0, util_1.isDefined)(endNow) && !endNow && (react_1.default.createElement(ui_1.Input, { size: ui_1.Input.SIZES.FULL_WIDTH, label: (0, util_1.t)('START'), required: true, type: 'date', onChange: setDeleteDate, min: (0, moment_1.default)().format('YYYY-MM-DD'), value: (0, moment_1.default)(deleteDate).format('YYYY-MM-DD') })),
                            displayDeleteEmployeeButton && (react_1.default.createElement(ui_1.Button, { testid: 'delete-employee-button', color: 'RED', onClick: () => {
                                    // submitTerminateEmployee
                                    terminateEmployeeModal();
                                }, loading: isLoadingTerminate }, (0, util_1.t)('DELETE_EMPLOYEE')))))))))),
        displayContent()));
};
exports.SettingsSection = SettingsSection;
